import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const TaskEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isAssignedToUser, setIsAssignedToUser] = useState(false);
  const [taskAcceptanceStatus, setTaskAcceptanceStatus] = useState("");

  const [formData, setFormData] = useState({
    taskDescription: "",
    expectedCompletedDate: "",
    assignedBy: "",
    assignedTo: "",
    taskStatus: "Pending",
    priority: "Medium",
    emailTrigger: "active",
    userRemarks: "",
    taskDuration: "",

    taskType: "one_time",
    reportType: "",
    weeklyDates: [],
    monthlyDate: "",
  });

  const [isLoading, setIsLoading] = useState(false);


  // Fetch task data
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!id) {
        toast.error("Task ID is missing", { autoClose: 2000 });
        navigate("/task");
        return;
      }

      setIsLoadingData(true);
      try {
        const payload = {
          action: "get-task-by-id",
          id: id,
        };

        const response = await axios.post(API_URL, payload);
        let responseData = response.data;

        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.data) {
          const task = responseData.data;

          // Check if current user is the assignedTo user
          const assignedToId = task.assignedTo?._id || task.assignedTo;
          const userId = localStorage.getItem("id");
          setIsAssignedToUser(assignedToId === userId);
          setTaskAcceptanceStatus(task.taskAcceptanceStatus || "Pending");

          setFormData({
            taskDescription: task.taskDescription || "",
            expectedCompletedDate: task.expectedCompletedDate
              ? new Date(task.expectedCompletedDate).toISOString().slice(0, 10)
              : "",
            assignedBy: task.assignedBy?._id || task.assignedBy || "",
            assignedTo: task.assignedTo?._id || task.assignedTo || "",
            taskStatus: task.taskStatus || "Pending",
            priority: task.priority || "Medium",
            emailTrigger: task.emailTrigger || "active",
            userRemarks: task.userRemarks || "",
            taskDuration: task.taskDuration || "",
            taskType: task.taskType || "one_time",
            reportType: task.reportType || "",
            weeklyDates: task.weeklyDates
              ? task.weeklyDates.map(
                  (date) => new Date(date).toISOString().split("T")[0],
                )
              : [],

            monthlyDate: task.monthlyDate
              ? new Date(task.monthlyDate).toISOString().split("T")[0]
              : "",
          });
        } else {
          toast.error(responseData.message || "Failed to fetch task details", {
            autoClose: 2000,
          });
          navigate("/task");
        }
      } catch (error) {
        console.error("Fetch task error:", error);
        toast.error("Failed to load task details", { autoClose: 2000 });
        navigate("/task");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchTaskData();
  }, [id, navigate]);

  // Fetch users for dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const payload = {
          action: "get-users",
          page: 1,
          limit: 1000,
        };
        const response = await axios.post(API_URL, payload);

        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.users) {
          setUsers(responseData.users);
        } else {
          toast.error(responseData.message || "Failed to fetch users", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch users error:", error);
        toast.error("Failed to load users", { autoClose: 2000 });
      }
    };

    fetchUsers();
  }, []);

  const handleWeeklyDateChange = (index, value) => {
    const dates = [...formData.weeklyDates];
    dates[index] = value;

    setFormData((prev) => ({
      ...prev,
      weeklyDates: dates,
    }));
  };

  const formatDate = (date) => {
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Simple form validation
  const validateForm = () => {
    if (isAssignedToUser) {
      // AssignedTo user only needs to provide taskStatus
      if (!formData.taskStatus) {
        toast.error("Task status is required", { autoClose: 2000 });
        return false;
      }
    } else {
      // AssignedBy user needs all required fields
      if (!formData.taskDescription.trim()) {
        toast.error("Task description is required", { autoClose: 2000 });
        return false;
      }
      if (!formData.expectedCompletedDate) {
        toast.error("Expected completion date is required", {
          autoClose: 2000,
        });
        return false;
      }
      if (!formData.assignedTo) {
        toast.error("Assigned to is required", { autoClose: 2000 });
        return false;
      }
    }
    return true;
  };
  const handleAcceptTask = async () => {
    try {
      const requestid = localStorage.getItem("id");

      const response = await axios.post(API_URL, {
        action: "accept-tasks",
        taskId: id,
        requestid,
      });

      if (response.data.success) {
        toast.success("Task accepted successfully");
        setTaskAcceptanceStatus("Accepted");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept task");
    }
  };

  const handleDeclineTask = async () => {
    try {
      const requestid = localStorage.getItem("id");

      const response = await axios.post(API_URL, {
        action: "decline-tasks",
        taskId: id,
        requestid,
      });

      if (response.data.success) {
        toast.success("Task declined successfully");
        setTaskAcceptanceStatus("Declined");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to decline task");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    if (formData.taskType === "repeated" && formData.reportType === "weekly") {
      const dates = formData.weeklyDates.filter(Boolean);

      if (new Set(dates).size !== dates.length) {
        toast.error("Duplicate weekly dates are not allowed");
        return;
      }
    }
    if (isAssignedToUser && taskAcceptanceStatus !== "Accepted") {
      toast.error("Please accept the task before updating.");
      return;
    }

    setIsLoading(true);

    try {
      let payload = {
        action: "update-task",
        id: id,
        requestid: currentUserId,
      };

      // If user is assignedTo, only send taskStatus and userRemarks
      if (isAssignedToUser) {
        payload.taskStatus = formData.taskStatus;
        payload.userRemarks = formData.userRemarks.trim();
        payload.emailTrigger = formData.emailTrigger;
      } else {
        // If user is assignedBy, send all fields
        payload = {
          ...payload,
          taskDescription: formData.taskDescription.trim(),
          expectedCompletedDate: formData.expectedCompletedDate,
          assignedTo: formData.assignedTo,
          taskStatus: formData.taskStatus,
          priority: formData.priority,
          emailTrigger: formData.emailTrigger,
          userRemarks: formData.userRemarks.trim(),
          taskType: formData.taskType,
          reportType:
            formData.taskType === "repeated" ? formData.reportType : null,

          weeklyDates:
            formData.taskType === "repeated" && formData.reportType === "weekly"
              ? formData.weeklyDates
                  .filter(Boolean)
                  .map((date) => formatDate(date))
              : [],

          monthlyDate:
            formData.taskType === "repeated" &&
            formData.reportType === "monthly"
              ? formatDate(formData.monthlyDate)
              : null,
        };
      }

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true) {
        toast.success(responseData.message || "Task updated successfully", {
          autoClose: 2000,
        });
        setTimeout(() => {
          navigate("/task");
        }, 2000);
      } else {
        toast.error(responseData.message || "Failed to update task", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Update task error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while updating task",
          {
            autoClose: 2000,
          },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          {
            autoClose: 2000,
          },
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Task</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/task">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Task</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/task">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {isAssignedToUser && (
                        <div className="alert alert-info">
                          <i className="fa fa-info-circle" /> As the assigned
                          user, you can only update the task status and user
                          remarks.
                        </div>
                      )}

                      {isAssignedToUser ? (
                        <>
                          {/* Read-only fields for assignedTo user */}
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Description
                            </label>
                            <div className="col-12 col-md-8">
                              <div className="txtdsply">
                                {formData.taskDescription || "-"}
                              </div>
                            </div>
                          </div>

                          <div className="form-group row align-items-center">
                            <label className="col-12 col-md-4 col-form-label">
                              Expected Completion Date
                            </label>
                            <div className="col-12 col-md-8">
                              <div className="txtdsply">
                                {formData.expectedCompletedDate
                                  ? new Date(
                                      formData.expectedCompletedDate,
                                    ).toLocaleDateString()
                                  : "-"}
                              </div>
                            </div>
                          </div>

                          <div className="form-group row align-items-center">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Duration
                            </label>
                            <div className="col-12 col-md-8">
                              <div className="txtdsply">
                                {formData.taskDuration || "-"}
                              </div>
                            </div>
                          </div>

                          {/* Editable fields for assignedTo user */}
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Status
                            </label>
                            <div className="col-12 col-md-8">
                              <select
                                name="taskStatus"
                                className="form-control"
                                value={formData.taskStatus}
                                onChange={handleChange}
                                disabled={taskAcceptanceStatus === "Declined"}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Inprogress">Inprogress</option>
                                <option value="Completed">Completed</option>
                                <option value="Hold">Hold</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Email Trigger
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                name="emailTrigger"
                                className="form-control"
                                value={formData.emailTrigger}
                                onChange={handleChange}
                                disabled={taskAcceptanceStatus === "Declined"}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              User Remarks
                            </label>
                            <div className="col-12 col-md-8">
                              <textarea
                                name="userRemarks"
                                className="form-control"
                                placeholder="Enter any remarks or notes"
                                rows="3"
                                value={formData.userRemarks}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          {taskAcceptanceStatus === "Pending" && (
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">
                                Task Acceptance
                              </label>

                              <div className="col-12 col-md-8">
                                <button
                                  type="button"
                                  className="btn btn-success mr-2"
                                  onClick={handleAcceptTask}
                                >
                                  Accept
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={handleDeclineTask}
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* All editable fields for assignedBy user */}
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Description
                            </label>
                            <div className="col-12 col-md-8">
                              <textarea
                                name="taskDescription"
                                className="form-control"
                                placeholder="Enter task description"
                                rows="3"
                                value={formData.taskDescription}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Expected Completion Date
                            </label>
                            <div className="col-12 col-md-8">
                              <input
                                type="date"
                                name="expectedCompletedDate"
                                className="form-control"
                                value={formData.expectedCompletedDate}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Email Trigger
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                name="emailTrigger"
                                className="form-control"
                                value={formData.emailTrigger}
                                onChange={handleChange}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Assigned To
                            </label>
                            <div className="col-12 col-md-8">
                              <select
                                name="assignedTo"
                                className="form-control"
                                value={formData.assignedTo}
                                onChange={handleChange}
                                required
                              >
                                <option value="">Select User</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Status
                            </label>
                            <div className="col-12 col-md-8">
                              <select
                                name="taskStatus"
                                className="form-control"
                                value={formData.taskStatus}
                                onChange={handleChange}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Inprogress">Inprogress</option>
                                <option value="Completed">Completed</option>
                                <option value="Hold">Hold</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Type
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                name="taskType"
                                className="form-control"
                                value={formData.taskType}
                                onChange={handleChange}
                              >
                                <option value="one_time">One Time</option>
                                <option value="repeated">Repeated</option>
                              </select>
                            </div>
                          </div>
                          {formData.taskType === "repeated" && (
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">
                                Report Type
                              </label>

                              <div className="col-12 col-md-8">
                                <select
                                  name="reportType"
                                  className="form-control"
                                  value={formData.reportType}
                                  onChange={handleChange}
                                >
                                  <option value="">Select</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                </select>
                              </div>
                            </div>
                          )}
                          {formData.taskType === "repeated" &&
                            formData.reportType === "weekly" && (
                              <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">
                                  Weekly Dates
                                </label>

                                <div className="col-12 col-md-8">
                                  {[0, 1, 2, 3].map((item) => (
                                    <input
                                      key={item}
                                      type="date"
                                      className="form-control mb-2"
                                      value={formData.weeklyDates[item] || ""}
                                      onChange={(e) =>
                                        handleWeeklyDateChange(
                                          item,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          {formData.taskType === "repeated" &&
                            formData.reportType === "monthly" && (
                              <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">
                                  Monthly Date
                                </label>

                                <div className="col-12 col-md-8">
                                  <input
                                    type="date"
                                    name="monthlyDate"
                                    className="form-control"
                                    value={formData.monthlyDate}
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                            )}
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Priority
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                name="priority"
                                className="form-control"
                                value={formData.priority}
                                onChange={handleChange}
                              >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                                <option value="Critical">Critical</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Email Trigger
                            </label>

                            <div className="col-12 col-md-8">
                              <select
                                name="emailTrigger"
                                className="form-control"
                                value={formData.emailTrigger}
                                onChange={handleChange}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Task Duration
                            </label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                name="taskDuration"
                                className="form-control"
                                placeholder="e.g., 2 days, 1 week"
                                value={formData.taskDuration}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              User Remarks
                            </label>
                            <div className="col-12 col-md-8">
                              <textarea
                                name="userRemarks"
                                className="form-control"
                                placeholder="Enter any remarks or notes"
                                rows="3"
                                value={formData.userRemarks}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskEdit;
